import { theme } from '@Styles/MuiTheme';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import { createColumnHelper } from '@tanstack/react-table';
import _isNil from 'lodash/isNil';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '@Components/Button/Button';
import CircularLoading from '@Components/CircularLoading/CircularLoading';
import DeleteDialog from '@Components/DeleteDialog/DeleteDialog';
import { DumbSettingsTable } from '@Components/Settings/components/SettingsTable/DumbSettingsTable';
import Styles from '@Components/Settings/views/SettingsViews.scss';
import ProjectsApi from '@Data/projects/Api';
import { IProjectDocument } from '@Data/projects/Interfaces';
import {
    selectedProjectDocumentsState,
    selectedProjectState,
} from '@Data/projects/Reducer';
import { selectedTeamState } from '@Data/teams/Reducer';
import { ISettingsBaseComponentProps } from '../../Interfaces';
import { ManageProjectDocumentDialog } from './ManageProjectDocumentDialog';

export const ProjectDocumentsTable = (props: ISettingsBaseComponentProps) => {
    const { title, isSubmitting, setIsSubmitting, id } = props;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
        useState<boolean>(false);
    const [isManageDialogOpen, setIsManageDialogOpen] =
        useState<boolean>(false);
    const [selectedDocument, setSelectedDocument] =
        useState<IProjectDocument>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const selectedTeam = useSelector(selectedTeamState);
    const selectedProject = useSelector(selectedProjectState);
    const projectDocuments = useSelector(selectedProjectDocumentsState);

    useEffect(() => {
        if (!_isNil(selectedProject)) {
            getProjectDocuments();
        }
    }, [selectedProject]);

    const getProjectDocuments = async () => {
        setIsLoading(true);
        await ProjectsApi.loadProjectDocuments(selectedProject);
        setIsLoading(false);
    };

    const handleEditDocument = (doc: IProjectDocument) => {
        setSelectedDocument(doc);
        setIsManageDialogOpen(true);
    };

    const handleOpenDeleteDocumentDialog = (doc: IProjectDocument) => {
        setSelectedDocument(doc);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteDocument = async () => {
        setIsProcessing(true);
        await ProjectsApi.deleteProjectDocument(
            selectedProject,
            selectedTeam,
            selectedDocument,
            projectDocuments
        );
        setIsProcessing(false);
        handleCloseDialogs();
    };

    const handleUpsertProjectDocument = async (name: string, url: string) => {
        setIsProcessing(true);
        if (_isNil(selectedDocument)) {
            await ProjectsApi.createProjectDocument(
                selectedProject,
                selectedTeam,
                projectDocuments,
                name,
                url
            );
        } else {
            await ProjectsApi.editProjectDocument(
                selectedProject,
                selectedTeam,
                selectedDocument,
                projectDocuments,
                name,
                url
            );
        }
        setIsProcessing(false);
        handleCloseDialogs();
    };

    const handleCloseDialogs = async (): Promise<void> => {
        setIsDeleteDialogOpen(false);
        setIsManageDialogOpen(false);
        return new Promise((resolve) => {
            setTimeout(() => {
                setSelectedDocument(null);
                resolve();
            }, theme.transitions.duration.standard);
        });
    };

    const columnHelper = createColumnHelper<IProjectDocument>();
    const columns = useMemo(() => {
        return [
            columnHelper.accessor('name', {
                cell: (info) => {
                    const rawData = info.row.original;
                    return (
                        <a href={rawData.url} target="_blank" rel="noreferrer">
                            {rawData.name}
                        </a>
                    );
                },
            }),
            columnHelper.accessor('id', {
                cell: (info) => {
                    const rawData = info.row.original;
                    return (
                        <div className={Styles.iconsContainer}>
                            <EditIcon
                                color="gradientGrey"
                                fontSize="small"
                                onClick={() => handleEditDocument(rawData)}
                            />
                            <DeleteIcon
                                fontSize="small"
                                color="red"
                                onClick={() =>
                                    handleOpenDeleteDocumentDialog(rawData)
                                }
                            />
                        </div>
                    );
                },
                size: 10,
            }),
        ];
    }, []);

    let innerHtml = <CircularLoading />;

    if (!isLoading) {
        innerHtml = _isNil(projectDocuments) ? (
            <span>No Project Documents have been added.</span>
        ) : (
            <DumbSettingsTable
                id={`${id}-table`}
                columns={columns}
                data={projectDocuments}
                hideTableHeader
            />
        );
    }

    return (
        <>
            <div className={Styles.condensedSettingsViewWrapper}>
                <div className={Styles.settingsViewHeader}>
                    <span id={`${id}-title`}>{title}</span>
                    <Button
                        color="blue"
                        onClick={() => setIsManageDialogOpen(true)}
                    >
                        Add New Document
                    </Button>
                </div>
                <div className={Styles.tableContainer}>{innerHtml}</div>
            </div>
            <DeleteDialog
                isOpen={isDeleteDialogOpen}
                onClose={handleCloseDialogs}
                onDelete={handleDeleteDocument}
                isDeleting={isProcessing}
                entityName={
                    selectedDocument
                        ? `the ${selectedDocument.name} document`
                        : ''
                }
            />
            <ManageProjectDocumentDialog
                isOpen={isManageDialogOpen}
                onClose={handleCloseDialogs}
                onSubmit={handleUpsertProjectDocument}
                isSubmitting={isProcessing}
                document={selectedDocument}
            />
        </>
    );
};
